import { useTranslation } from 'next-i18next';

/**
 * 코드(언어/지역)를 현재 UI 로케일 이름으로 조합한다. 실패 시 fallback → 코드 원문.
 * 구형 브라우저(Intl.DisplayNames 미지원)·malformed 코드에서도 throw 하지 않는다.
 * 근거는 커밋 메시지 참고(브라우저 커버리지·큐레이션 대비 트레이드오프).
 */
function safeDisplayName(
  locale: string,
  type: 'language' | 'region',
  code?: string,
  fallback?: string,
) {
  const fb = fallback ?? code ?? '';
  if (!code) return fb;
  if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined')
    return fb;
  try {
    return new Intl.DisplayNames([locale], { type }).of(code) ?? fb;
  } catch {
    return fb;
  }
}

/**
 * 중국어 변종 override — Intl 의 region 기반 표기가 우리 케이스에 부적절해 script 기반으로 교체.
 * - zh-CN/zh-TW: Intl 은 "중국어(중국)/(대만)" → script 코드로 "중국어(간체)/(번체)" 로.
 * - zh-HK: zh-Hant-HK 도 "홍콩[중국 특별행정구]" 정치 표기가 남아 → zh-Hant + " (HK)" 로 조합
 *   (정치 표기 회피 + zh-TW(번체)와 구분). 근거·실측은 커밋 메시지 참고.
 */
const LANGUAGE_INTL_ALIAS: Record<string, string> = {
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
  'zh-HK': 'zh-Hant',
};

/**
 * 언어 코드 → 현재 UI 로케일 언어명. 번역 시트(common.language.*) 대신 Intl 로 조합한다.
 * 중국어 변종만 위 override 적용. `(code, fallback?) => name`.
 */
export function useLanguageName() {
  const { i18n } = useTranslation();
  const locale = i18n?.language ?? 'en';
  return (code?: string, fallback?: string) => {
    if (!code) return fallback ?? '';
    const alias = LANGUAGE_INTL_ALIAS[code] ?? code;
    // Intl 실패 시 '' 를 받아 원본 코드로 폴백(alias 노출 방지).
    const name = safeDisplayName(locale, 'language', alias, '');
    if (!name) return fallback ?? code;
    return code === 'zh-HK' ? `${name} (HK)` : name;
  };
}

/**
 * 국가 코드 → 현재 UI 로케일 국가명. 큐레이션(country 네임스페이스)에서 조합한다.
 * 국가명은 KR/CN/TW/HK 등 정치 민감·전면 노출이라 Intl 대신 시트 큐레이션을 쓴다.
 * `(code, fallback?) => name`. defaultValue 로 미등록 코드도 무해화(missingKeyHandler 경고 X).
 *
 * country 는 i18next `CustomTypeOptions.resources` 타입에는 등록하지 않는다 — ns 를 하나 더
 * 넣으면 TFunction 키 추론이 TS2589(excessively deep)로 터진다(13개에서 이미 임계). 런타임
 * ns 는 serverSideTranslations 로 로드되므로, 여기선 raw i18n.t 를 좁은 시그니처로 캐스팅해 쓴다.
 */
export function useCountryName() {
  const { i18n } = useTranslation();
  const translate = i18n?.t as
    | ((key: string, opts?: { defaultValue?: string }) => string)
    | undefined;
  return (code?: string, fallback?: string) => {
    if (!code) return fallback ?? '';
    const upper = code.toUpperCase();
    return (
      translate?.(`country:${upper}`, { defaultValue: fallback ?? code }) ??
      fallback ??
      code
    );
  };
}
